import React, { useEffect, useMemo, useState } from "react";
import { Col, Row, Table, Button } from "reactstrap";
import { AiFillDelete, AiFillEdit, AiOutlineSearch } from "react-icons/ai";
import { Modal, Input } from "antd";

import SideBar from "../../components/SideBar";
import Header from "../../components/DataManagement/Responsable/Header";

import { getAllRH, updateRHStatus, updateRH, deleteOneRH } from "./../../api/data/RH";

import Swal from "sweetalert2";

function ResponsableTable() {
  const [RHData, setRHData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [editModal, setEditModal] = useState(false);
  const [editData, setEditData] = useState(null);

  const getRHData = async () => {
    try {
      setIsLoading(true);
      setHasError(false);

      const response = await getAllRH();

      if (response && !Array.isArray(response)) {
        setHasError(true);
        setRHData([]);
        return;
      }

      setRHData(Array.isArray(response) ? response : []);
    } catch (err) {
      setHasError(true);
      setRHData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredData = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return RHData;

    return RHData.filter(el =>
      [el.firstName, el.lastName, el.grade, el.fonction, el.cin, el.email]
        .filter(Boolean)
        .some(field => field.toLowerCase().includes(term))
    );
  }, [RHData, searchTerm]);

  const handleEditClick = (el) => {
    setEditData({
      _id: el._id,
      firstName: el.firstName,
      lastName: el.lastName,
      grade: el.grade,
      fonction: el.fonction,
      cin: el.cin,
      ppr: el.ppr,
      phone: el.phone,
      permisNumber: el.permisNumber,
      userName: el.userName,
      email: el.email,
    });
    setEditModal(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditData({ ...editData, [name]: value });
  };

  const handleUpdateSubmit = async () => {
    try {
      const { _id, ...data } = editData;
      const response = await updateRH(_id, data);
      if (response) {
        setEditModal(false);
        getRHData();
        Swal.fire("Modifié", "", "success");
      }
    } catch (err) {
      Swal.fire("Erreur", err?.response?.data?.error || "", "error");
    }
  };

  const handleStateChange = async (id, status) => {
    try {
      Swal.fire({
        title: status === 'V' ? 'Voulez-vous valider cette opération ?' : 'Voulez-vous annuler cette opération ?',
        icon: "warning",
        confirmButtonColor: status === 'V' ? "#198754" : "#dc3545",
        confirmButtonText: status === 'V' ? "Valider" : "Annuler",
      }).then(async (result) => {
        if (result.isConfirmed) {
            try {
                const response = await updateRHStatus(id, status)
                if(response) {
                  getRHData()
                  Swal.fire(status === 'V' ? "Validée" : "Annulée", "", "success");
                }
            }
            catch(err) {
                Swal.fire("Erreur", "", "error");
            }
        }
      });
    } catch (err) {}
  };

  const handleDelete = async (id) => {
    try {
        Swal.fire({
          title: 'Voulez-vous supprimer ce responsable ?',
          icon: "warning",
          confirmButtonColor: "#dc3545",
          confirmButtonText: "Supprimer",
        }).then(async (result) => {
          if (result.isConfirmed) {
            try {
                const response = await deleteOneRH(id)
                if(response) {
                  getRHData()
                  Swal.fire("Supprimé", "", "success");
                }
            }
            catch(err) {
                Swal.fire("Erreur", "", "error");
            }
          }
        });
      } catch (err) {}
  }

  useEffect(() => {
    getRHData();
  }, []);

  return (
    <section className="responsableTableSection">
      <div className="contents">
        <Row>
          <Col md="2" className="p-0">
            <SideBar />
          </Col>
          <Col md="10" className="p-0">
            <Header />

            <div className="tableSection">
              <div className="tableToolbar">
                <Input
                  prefix={<AiOutlineSearch />}
                  placeholder="Rechercher un responsable, un grade, une fonction…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  allowClear
                />
              </div>

              <div className="table-wrapper">
                {isLoading && (
                  <p className="stateMessage">Chargement des responsables…</p>
                )}

                {!isLoading && hasError && (
                  <p className="stateMessage error">Impossible de charger les responsables. Réessayez dans un instant.</p>
                )}

                {!isLoading && !hasError && RHData.length === 0 && (
                  <p className="stateMessage">Aucun responsable enregistré. Cliquez sur « Ajouter un responsable » pour commencer.</p>
                )}

                {!isLoading && !hasError && RHData.length > 0 && filteredData.length === 0 && (
                  <p className="stateMessage">Aucun responsable ne correspond à « {searchTerm} ».</p>
                )}

                {!isLoading && !hasError && filteredData.length > 0 && (
                  <Table striped>
                    <thead>
                      <tr>
                        <th>Nom</th>
                        <th>Grade</th>
                        <th>Fonction</th>
                        <th>Carte nationale</th>
                        <th>Email</th>
                        <th>Etat d'opération</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredData.map((el) => (
                        <tr key={el._id}>
                          <td>{el.firstName} {el.lastName}</td>
                          <td>{el.grade}</td>
                          <td>{el.fonction}</td>
                          <td>{el.cin}</td>
                          <td>{el.email}</td>
                          <td>
                            {el.status === "N" && (
                              <div className="btnStatus">
                                <Button size="sm" color="success" onClick={() => handleStateChange(el._id, 'V')}>Valider</Button>
                                <Button size="sm" color="danger" onClick={() => handleStateChange(el._id, 'A')}>Annuler</Button>
                              </div>
                            )}
                            {el.status === "V" && <span className="status-badge badge-valid">✓ Validée</span>}
                            {el.status === "A" && <span className="status-badge badge-canceled">✕ Annulée</span>}
                          </td>
                          <td>
                            <div className="action-icons">
                              <button className="action-btn edit-btn" onClick={() => handleEditClick(el)}><AiFillEdit size="1rem" /></button>
                              <button className="action-btn delete-btn" onClick={() => handleDelete(el._id)}><AiFillDelete size="1rem" /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </div>

      <Modal
        title="Modifier le responsable"
        open={editModal}
        onOk={handleUpdateSubmit}
        onCancel={() => setEditModal(false)}
        okText="Enregistrer"
        cancelText="Annuler"
      >
        {editData && (
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <Input placeholder="Prénom" name="firstName" value={editData.firstName} onChange={handleEditChange} />
            <Input placeholder="Nom" name="lastName" value={editData.lastName} onChange={handleEditChange} />
            <Input placeholder="Grade" name="grade" value={editData.grade} onChange={handleEditChange} />
            <Input placeholder="Fonction" name="fonction" value={editData.fonction} onChange={handleEditChange} />
            <Input placeholder="CIN" name="cin" value={editData.cin} onChange={handleEditChange} />
            <Input placeholder="PPR" name="ppr" value={editData.ppr} onChange={handleEditChange} />
            <Input placeholder="Téléphone" name="phone" value={editData.phone} onChange={handleEditChange} />
            <Input placeholder="N° permis" name="permisNumber" value={editData.permisNumber} onChange={handleEditChange} />
            <Input placeholder="Nom d'utilisateur" name="userName" value={editData.userName} onChange={handleEditChange} />
            <Input placeholder="Email" name="email" value={editData.email} onChange={handleEditChange} />
          </div>
        )}
      </Modal>
    </section>
  );
}

export default ResponsableTable;
